/**
 * Which card-entry surface checkout renders, and which body it is allowed to post.
 *
 * Card #53947 (the one permitted recovery for #53493). Both decisions below were written inline
 * in `CheckoutPage.jsx`, where NOTHING can execute them: jest here runs `testEnvironment: 'node'`
 * with `transform: {}` and no `jsx` in `moduleFileExtensions`, so a test cannot so much as import
 * that file. Extracting them is what makes the Stripe arm and the incumbent arm provable WITHOUT
 * a browser and WITHOUT Stripe credentials — the acceptance this card is scoped to. Same shape,
 * and the same reason, as `web/utils/placeOrderBlockers.js`, which `CheckoutPage.jsx` already
 * imports for its own pure derivation.
 *
 * Pure: no environment read, no I/O, no module-scope state. The config is passed IN, so the
 * client and the server cannot drift into disagreeing about which body is legal.
 *
 * ROLLBACK: inline these two expressions back into `CheckoutPage.jsx` and delete this file plus
 * `tests/unit/checkoutStripeArmRouting.test.js`. Behaviour is unchanged either way.
 *
 * Card #54415 adds the SHAPE gate (`isRealShapedPublishableKey`) and `deriveCardEntryError`.
 * ROLLBACK for #54415 only: restore `deriveIsStripeArm`'s second clause to
 * `Boolean(paymentConfig?.publishableKey)`, and in `CheckoutPage.jsx` drop `cardEntryError` from
 * the render + `validateStep2`. That restores the infinite "Preparing secure card entry..." hang.
 */

/**
 * Card #54415 (D3). PRESENCE IS NOT SHAPE — this is the check the old gate was missing.
 *
 * Measured cost of the old `Boolean(paymentConfig?.publishableKey)`: the live `.env` ships the
 * placeholder `pk_test_your…-stripe-publishable-key`. That is a NON-EMPTY STRING, so it passed the
 * gate, `loadStripe()` was handed a key the provider rejects, and the page sat on
 * "Preparing secure card entry..." forever — a dead checkout, not a fallback.
 *
 * A publishable key is real-shaped when it is a `pk_` key for test or live mode whose body is
 * [A-Za-z0-9_] and carries no placeholder marker. ⛔ A `sk_` secret key is NOT a publishable key;
 * accepting one here would ship a secret to the browser, so the prefix check is part of the rule.
 *
 * ⚠️ This is a SHAPE check, deliberately not a liveness check: proving a key works needs the
 * provider's network and real credentials, both excluded by the wave's test-keys-only constraint.
 * It answers exactly the question that hung the page — "is this obviously not a key?".
 */
const PUBLISHABLE_KEY_PREFIX = /^pk_(?:test|live)_/;
const PUBLISHABLE_KEY_BODY = /^[A-Za-z0-9_]{8,}$/;
const PUBLISHABLE_KEY_PLACEHOLDER = /your|placeholder|change_?me|dummy|example|sample|fake|xxx/i;

export const isRealShapedPublishableKey = (publishableKey) => {
  if (typeof publishableKey !== 'string') return false;
  const prefix = publishableKey.match(PUBLISHABLE_KEY_PREFIX);
  if (!prefix) return false;
  const body = publishableKey.slice(prefix[0].length);
  return PUBLISHABLE_KEY_BODY.test(body) && !PUBLISHABLE_KEY_PLACEHOLDER.test(body);
};

/**
 * The NAMED state checkout renders in place of the card surface when the switch says the money
 * path is the hosted provider but its key is unusable. The customer must never see an infinite
 * spinner, and must never be handed the raw-card form either: the SERVER is routing to the
 * hosted provider, so a PAN posted from that form is refused 400 `RAW_CARD_NOT_ACCEPTED`.
 */
export const CARD_ENTRY_KEY_UNUSABLE = 'PAYMENT_KEY_UNUSABLE';
export const CARD_ENTRY_KEY_UNUSABLE_MESSAGE =
  'Card payments are temporarily unavailable — the payment configuration on this site is incomplete. Please try again, or contact support if this continues.';

/**
 * 🔑 `defaultProvider`, ⛔ NEVER `provider` — #53493 MEASURED THE COST OF CONFUSING THEM.
 *
 * `provider` tracks `STRIPE_ENABLED`: "can we SAVE a card?". `defaultProvider` is
 * `resolveDefaultProvider`: "who will TAKE this payment?". They disagree on exactly the
 * configuration this box ships — `.env.sandbox` sets `STRIPE_ENABLED=true` with
 * `PAYMENT_PROVIDER_DEFAULT=authorize_net` — and routing on `provider` hid the raw card fields
 * on the INCUMBENT arm, whose server then answered 400 `card is required`.
 *
 * ⛔ BOTH conditions are required. A provider alone would mount a hosted Element with no
 * publishable key, i.e. an empty card surface the customer cannot pay through.
 *
 * FAILS CLOSED: an absent config (the request failed) yields `false`, which renders the raw-card
 * form that has always worked rather than blanking the surface someone is trying to pay with.
 */
export const deriveIsStripeArm = (paymentConfig) =>
  paymentConfig?.defaultProvider === 'stripe' &&
  isRealShapedPublishableKey(paymentConfig?.publishableKey);

/**
 * The named failure a caller renders instead of guessing, or `null` when there is nothing wrong.
 *
 * ⛔ Only when `defaultProvider === 'stripe'`: a bad key on the incumbent arm is irrelevant — that
 * arm never mounts the Element — and reporting it would scare a customer on a surface that pays
 * perfectly well.
 */
export const deriveCardEntryError = (paymentConfig) =>
  paymentConfig?.defaultProvider === 'stripe' &&
  !isRealShapedPublishableKey(paymentConfig?.publishableKey)
    ? { code: CARD_ENTRY_KEY_UNUSABLE, message: CARD_ENTRY_KEY_UNUSABLE_MESSAGE }
    : null;

/**
 * The two arms send DIFFERENT things, and ⛔ must never send both.
 *
 *   Stripe    — a top-level `paymentMethodId` and NO `card` key at all. The server refuses a PAN
 *               here 400 `RAW_CARD_NOT_ACCEPTED`, and requires the reference 400
 *               `PAYMENT_METHOD_REQUIRED`.
 *   Incumbent — the raw `card` object it has always taken, byte-identical to before, and no
 *               `paymentMethodId` (#53299 is explicit that this path is NOT deleted).
 *
 * The Stripe body is built from the minted reference ALONE, so a PAN cannot reach the request
 * even if `cardData` still held one from an earlier arm switch.
 */
export const buildCardPaymentPayload = ({ isStripeArm, paymentMethodId, cardData }) =>
  isStripeArm
    ? { paymentMethodId }
    : {
        card: {
          number: cardData.number.replace(/\s/g, ''),
          expiration: cardData.expiration,
          cvv: cardData.cvv
        }
      };

/**
 * Card #54400 (D4). The publishable key a hosted card Element should mount with, or `null` when
 * the incumbent's raw fields are the surface — the SAME routing decision as `deriveIsStripeArm`,
 * answered in the one shape a caller that renders the Element needs. Lets the refill modal route
 * on the arm without naming the provider itself.
 */
export const deriveHostedCardKey = (paymentConfig) =>
  (deriveIsStripeArm(paymentConfig) ? paymentConfig.publishableKey : null);
